import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import PageHeader from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { Calendar, Clock, Check, Loader2 } from 'lucide-react';

function nextDays(n) {
  const out = [];
  for (let i = 1; i <= n; i++) {
    const d = new Date(); d.setDate(d.getDate() + i);
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
}

export default function BookingPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({
    category_slug: '', sub_service_slug: '', date: '', time_slot: '',
    address: '', city: '', notes: '',
    customer_name: user?.name || '', customer_email: user?.email || '', customer_phone: user?.phone || '',
  });
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const days = useMemo(() => nextDays(14), []);

  useEffect(() => { api.get('/services').then(r => setCats(r.data.categories || [])); }, []);
  useEffect(() => {
    if (form.date) {
      api.get('/bookings/availability', { params: { date: form.date } }).then(r => setSlots(r.data.slots || []));
    } else { setSlots([]); }
  }, [form.date]);

  const category = cats.find(c => c.slug === form.category_slug);
  const sub = category?.sub_services.find(s => s.slug === form.sub_service_slug);
  const canSubmit = form.category_slug && form.sub_service_slug && form.date && form.time_slot && form.customer_name && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.customer_email);

  const submit = async () => {
    setLoading(true);
    try {
      const bookingResp = await api.post('/bookings', {
        service_name: category.name,
        sub_service_name: sub.name,
        date: form.date, time_slot: form.time_slot,
        address: form.address, city: form.city, notes: form.notes,
        customer_name: form.customer_name, customer_email: form.customer_email, customer_phone: form.customer_phone,
      });
      const bookingId = bookingResp.data.booking.id;
      // Create Stripe Checkout Session for the $50 deposit
      const checkout = await api.post('/stripe/checkout', {
        purpose: 'booking_deposit',
        booking_id: bookingId,
      });
      window.location.href = checkout.data.url; // redirect to Stripe
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Booking failed');
      setLoading(false);
    }
  };

  if (done) {
    return (
      <Layout>
        <PageHeader eyebrow="Booking Confirmed" title="You're scheduled!" subtitle="We'll send a confirmation email shortly. Your $50 deposit has been received — it's applied toward your service total." />
        <section className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
          <div className="rounded-2xl border border-[#E6E9EE] bg-white p-6">
            <div className="flex items-center gap-3"><Check className="h-6 w-6 text-[#17352D]" /><div><div className="font-headline text-2xl text-[#17352D]">{sub.name}</div><div className="text-sm text-slate-600">{form.date} @ {form.time_slot}</div></div></div>
            <div className="mt-4 flex gap-2"><Button onClick={() => nav(user ? '/portal' : '/')} className="rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white">{user ? 'My Portal' : 'Home'}</Button><Button onClick={() => { setDone(false); setForm(f => ({ ...f, time_slot: '' })); }} variant="outline" className="rounded-xl">Book another</Button></div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHeader eyebrow="Book Service" title="Pick a service & time that works for you" subtitle="A $50 deposit holds your spot. Refundable up to 24 hours before your appointment." />
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-5" data-testid="booking-form">
        <div className="rounded-2xl border border-[#E6E9EE] bg-white p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label>Service category</Label>
              <Select value={form.category_slug} onValueChange={(v) => setForm({ ...form, category_slug: v, sub_service_slug: '' })}>
                <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="booking-category"><SelectValue placeholder="Choose" /></SelectTrigger>
                <SelectContent>{cats.map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Sub-service</Label>
              <Select value={form.sub_service_slug} onValueChange={(v) => setForm({ ...form, sub_service_slug: v })} disabled={!category}>
                <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="booking-subservice"><SelectValue placeholder="Choose" /></SelectTrigger>
                <SelectContent>{category?.sub_services.map(s => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-[#E6E9EE] bg-white p-5">
          <div className="flex items-center gap-2 mb-3"><Calendar className="h-4 w-4 text-[#C8922F]" /><Label className="m-0">Date</Label></div>
          <div className="flex gap-2 overflow-x-auto -mx-1 px-1">
            {days.map(d => (
              <button key={d} type="button" onClick={() => setForm({ ...form, date: d, time_slot: '' })}
                data-testid={`booking-date-${d}`}
                className={`shrink-0 rounded-xl border px-3 py-2 text-center transition-colors-only ${form.date === d ? 'border-[#17352D] bg-[#17352D] text-white' : 'border-[#E6E9EE] bg-white hover:bg-[#F2F7F5] text-slate-700'}`}>
                <div className="text-[10px] uppercase tracking-wider">{new Date(d).toLocaleDateString('en-US', { weekday: 'short' })}</div>
                <div className="font-semibold">{new Date(d).getDate()}</div>
                <div className="text-[10px] opacity-80">{new Date(d).toLocaleDateString('en-US', { month: 'short' })}</div>
              </button>
            ))}
          </div>
          {form.date && (
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-2"><Clock className="h-4 w-4 text-[#C8922F]" /><Label className="m-0">Time slot</Label></div>
              <div className="grid grid-cols-4 gap-2">
                {slots.map(s => (
                  <button key={s.time} type="button" disabled={!s.available} onClick={() => setForm({ ...form, time_slot: s.time })}
                    data-testid={`booking-slot-${s.time}`}
                    className={`rounded-lg border py-2 text-sm ${form.time_slot === s.time ? 'border-[#17352D] bg-[#17352D] text-white' : s.available ? 'border-[#E6E9EE] hover:bg-[#F2F7F5]' : 'border-[#E6E9EE] bg-slate-50 text-slate-300 cursor-not-allowed'}`}>{s.time}</button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-[#E6E9EE] bg-white p-5 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label>Your name</Label><Input required value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} className="h-11 rounded-xl mt-1" data-testid="booking-name" /></div>
            <div><Label>Email</Label><Input type="email" required value={form.customer_email} onChange={e => setForm({ ...form, customer_email: e.target.value })} className="h-11 rounded-xl mt-1" data-testid="booking-email" /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label>Phone</Label><Input value={form.customer_phone} onChange={e => setForm({ ...form, customer_phone: e.target.value })} className="h-11 rounded-xl mt-1" data-testid="booking-phone" /></div>
            <div><Label>City</Label><Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className="h-11 rounded-xl mt-1" data-testid="booking-city" /></div>
          </div>
          <div><Label>Address</Label><Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className="h-11 rounded-xl mt-1" data-testid="booking-address" /></div>
          <div><Label>Notes (optional)</Label><Textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="rounded-xl mt-1" data-testid="booking-notes" /></div>
        </div>

        <div className="flex items-center justify-between rounded-2xl border border-[#EED7A6] bg-[#FBF7EE] p-4">
          <div><div className="text-xs uppercase tracking-[0.18em] text-[#C8922F] font-semibold">Total today</div><div className="font-headline text-2xl text-[#17352D]">$50.00 <span className="text-sm text-slate-500">(refundable deposit)</span></div></div>
          <Button onClick={submit} disabled={!canSubmit || loading} data-testid="booking-confirm-button" className="rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white h-12 px-6">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm & pay deposit'}</Button>
        </div>
      </section>
    </Layout>
  );
}
